import { useState } from "react";
import { toast } from "sonner";
import { Share2, Facebook, Instagram, Link2, MessageCircle, Twitter } from "lucide-react";

export default function ShareMenu({ url, text = "", image, testId = "share-menu", label, placement = "above" }) {
  const [open, setOpen] = useState(false);
  const eu = encodeURIComponent(url);
  const et = encodeURIComponent(text);

  const shareNative = async () => {
    try { await navigator.share({ title: "Pickle Bullpen", text, url }); } catch {}
    setOpen(false);
  };

  const shareInstagram = async () => {
    try {
      if (image) {
        const blob = await fetch(image).then((r) => r.blob());
        const file = new File([blob], "pickle-bullpen.png", { type: blob.type || "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ files: [file], text });
          setOpen(false);
          return;
        }
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "pickle-bullpen.png";
        a.click();
        toast.success("Image saved — open Instagram and post it from your camera roll");
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`.trim());
        toast.success("Caption + link copied — paste it into your Instagram post");
      }
    } catch { toast.error("Couldn't prepare the Instagram share"); }
    setOpen(false);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Copy failed"); }
    setOpen(false);
  };

  const itemCls = "w-full flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-300 hover:bg-pb-elevated hover:text-pb-red transition-colors text-left";

  const Item = ({ icon: Icon, children, onClick, tid }) => (
    <button data-testid={tid} onClick={onClick} className={itemCls}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </button>
  );

  // Real links (never popup-blocked) — open the network's share composer in a new tab
  const LinkItem = ({ icon: Icon, children, href, tid }) => (
    <a data-testid={tid} href={href} target="_blank" rel="noopener noreferrer"
      onClick={() => setOpen(false)} className={itemCls}>
      <Icon className="w-3.5 h-3.5" /> {children}
    </a>
  );

  return (
    <div className="relative inline-block">
      <button data-testid={testId} onClick={() => setOpen(!open)} title="Share"
        className="flex items-center gap-2 text-sm text-zinc-500 hover:text-[#4ADE80] transition-colors">
        <Share2 className="w-4 h-4" /> {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div data-testid={`${testId}-popover`}
            className={`absolute z-[61] w-52 bg-pb-surface border border-pb-border shadow-2xl animate-fade-up ${placement === "below" ? "top-full right-0 mt-2" : "bottom-full left-0 mb-2"}`}>
            {typeof navigator !== "undefined" && !!navigator.share && (
              <Item icon={Share2} onClick={shareNative} tid={`${testId}-native`}>Share…</Item>
            )}
            <LinkItem icon={Facebook} href={`https://www.facebook.com/sharer/sharer.php?u=${eu}`} tid={`${testId}-facebook`}>Facebook</LinkItem>
            <LinkItem icon={Twitter} href={`https://twitter.com/intent/tweet?url=${eu}&text=${et}`} tid={`${testId}-x`}>X / Twitter</LinkItem>
            <LinkItem icon={MessageCircle} href={`https://wa.me/?text=${et}%20${eu}`} tid={`${testId}-whatsapp`}>WhatsApp</LinkItem>
            <Item icon={Instagram} onClick={shareInstagram} tid={`${testId}-instagram`}>Instagram</Item>
            <Item icon={Link2} onClick={copyLink} tid={`${testId}-copy`}>Copy Link</Item>
          </div>
        </>
      )}
    </div>
  );
}
